import type { RawPacket, SensorPacket } from '../types'

const PACKET_BYTE_LENGTH = 48 // 12 × float32

/**
 * Decodes the NEENO IMU packet (neeno-imu-v1 profile).
 *
 * 12 consecutive float32 values, little-endian:
 *  offset  0 → timestamp
 *  offset  4 → quat_w
 *  offset  8 → quat_x
 *  offset 12 → quat_y
 *  offset 16 → quat_z
 *  offset 20 → accel_x
 *  offset 24 → accel_y
 *  offset 28 → accel_z
 *  offset 32 → gyro_x
 *  offset 36 → gyro_y
 *  offset 40 → gyro_z
 *  offset 44 → extra
 */
function decodeNeenoImuPacket(raw: RawPacket): SensorPacket | null {
  try {
    if (raw.byteLength < PACKET_BYTE_LENGTH) return null

    return {
      timestamp: raw.getFloat32(0, true),
      orientation: {
        w: raw.getFloat32(4, true),
        x: raw.getFloat32(8, true),
        y: raw.getFloat32(12, true),
        z: raw.getFloat32(16, true),
      },
      acceleration: {
        x: raw.getFloat32(20, true),
        y: raw.getFloat32(24, true),
        z: raw.getFloat32(28, true),
      },
      gyroscope: {
        x: raw.getFloat32(32, true),
        y: raw.getFloat32(36, true),
        z: raw.getFloat32(40, true),
      },
      extra: raw.getFloat32(44, true),
    }
  } catch {
    return null
  }
}

/**
 * Decoder registry — keyed by device profile ID.
 * Add new profiles here as new device types are introduced.
 */
type DecoderFn = (raw: RawPacket) => SensorPacket | null

const decoderRegistry: Record<string, DecoderFn> = {
  'neeno-imu-v1': decodeNeenoImuPacket,
}

export function decodePacket(profileId: string, raw: RawPacket): SensorPacket | null {
  const decoder = decoderRegistry[profileId]
  if (!decoder) {
    console.warn(`[Decoder] No decoder registered for profile: "${profileId}"`)
    return null
  }
  return decoder(raw)
}
